import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import { galerieALaction } from '../../auth/sujet.js';
import {
  registrePoliceApi,
  useCompteRegistre,
  usePropositionsRegistre,
  useReglagesRegistre,
  useMutationRegistre,
} from '../../api/registrePolice.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { ListeRegistre } from './ListeRegistre.js';
import styles from './RegistrePolice.module.css';

export function RegistrePolicePage() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const peutLire = galerieALaction(galerie, 'registre-police:read');
  const peutInscrire = galerieALaction(galerie, 'registre-police:inscrire');
  const peutRectifier = galerieALaction(galerie, 'registre-police:rectifier');
  const [page, setPage] = useState(1);
  const compte = useCompteRegistre(galerieSlug, peutLire);
  const propositions = usePropositionsRegistre(galerieSlug, page, peutLire);
  const reglages = useReglagesRegistre(galerieSlug, peutLire && peutRectifier);
  const regler = useMutationRegistre(galerieSlug, (valeur: boolean, appel) =>
    registrePoliceApi.regler(galerieSlug, valeur, appel),
  );
  const label = (cle: string) => t(`regie:registrePolice.${cle}`);
  return (
    <section className={styles.page} data-ui="page">
      <header className={styles.entete} data-ui="page-heading">
        <h1>{label('titre')}</h1>
        {peutLire && (
          <div className={styles.actions}>
            {peutInscrire && (
              <Link to={`/${galerieSlug}/registre-police/propositions/nouvelle`}>
                {label('inscrireObjet')}
              </Link>
            )}
            <Link to={`/${galerieSlug}/registre-police/controle`}>{label('controle')}</Link>
          </div>
        )}
      </header>
      {peutLire && (
        <section className={styles.propositions}>
          <h2>{label('propositions')}</h2>
          {compte.data && <p>{t('regie:registrePolice.compte', { count: compte.data.compte })}</p>}
          {(compte.isError || propositions.isError) && (
            <p role="alert">{t('commun:erreurReseau')}</p>
          )}
          {propositions.data?.propositions.map((proposition) => (
            <Link
              key={proposition.id}
              to={`/${galerieSlug}/registre-police/propositions/${proposition.id}`}
            >
              {proposition.brouillon.objet?.titre ||
                proposition.brouillon.objet?.nature ||
                label('objetSansDescription')}
            </Link>
          ))}
          {propositions.data && propositions.data.pagination.pages > 1 && (
            <div className={styles.actions}>
              <Button
                disabled={page <= 1 || propositions.isFetching}
                onClick={() => setPage(page - 1)}
              >
                {label('precedent')}
              </Button>
              <span>
                {page} / {propositions.data.pagination.pages}
              </span>
              <Button
                disabled={page >= propositions.data.pagination.pages || propositions.isFetching}
                onClick={() => setPage(page + 1)}
              >
                {label('suivant')}
              </Button>
            </div>
          )}
        </section>
      )}
      {peutRectifier && peutLire && (
        <div>
          {reglages.data && (
            <label>
              <input
                type="checkbox"
                checked={reglages.data.inscrireDepots}
                disabled={regler.isPending}
                onChange={(event) => regler.mutate(event.target.checked)}
              />{' '}
              {label('inscrireDepots')}
            </label>
          )}
          {reglages.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
          {regler.isError && (
            <p role="alert">
              {estApiError(regler.error) ? regler.error.message : t('commun:erreurReseau')}
            </p>
          )}
        </div>
      )}
      <ListeRegistre galerieSlug={galerieSlug} peutLire={peutLire} />
    </section>
  );
}
